/**
 * All codes created or modified here should reflect in the ERROR_CODES.md documentation.
 *
 * {@link https://github.azc.ext.hp.com/jarvis/react-portal-oobe-mfe/blob/develop/ERROR_CODES.md}
 */

import { PageSuffix } from './suffix'

import { ApiPrefix } from '@/constants/errors/prefix'
import { DeviceHttpProxyError } from '@/services/device-http-proxy'
import { DeviceHttpProxyPluginErrorType } from '@/services/device-http-proxy/types'

/**
 * Map DHP errors.
 * Each DHP Service should have specific error codes for known failure scenarios.
 * Prefixes in code:
 *  - `S` for request preparation and response parsing errors
 *  - `I` for internal plugin DHP errors
 */
export const DHP_ERROR_CODES = {
  [DeviceHttpProxyError.ERRORS.PathNotFound]: 'S0001',
  [DeviceHttpProxyError.ERRORS.FirmwareProtocolNotFound]: 'S0002',
  [DeviceHttpProxyError.ERRORS.ManifestResourceTypeNotFound]: 'S0003',
  [DeviceHttpProxyError.ERRORS.ServiceNotFound]: 'S0004',
  [DeviceHttpProxyError.ERRORS.ServiceNotAvailable]: 'S0005',
  [DeviceHttpProxyError.ERRORS.PluginNotAvailable]: 'S0006',
  [DeviceHttpProxyError.ERRORS.RequestFailure]: 'S0007',
  [DeviceHttpProxyError.ERRORS.RequestUnknownFailure]: 'S0008',
  [DeviceHttpProxyError.ERRORS.RequestStatusFailure]: 'S0009',
  [DeviceHttpProxyPluginErrorType.invalidArgument]: 'I0001',
  [DeviceHttpProxyPluginErrorType.invalidSession]: 'I0002',
  [DeviceHttpProxyPluginErrorType.sessionNotActive]: 'I0003',
  [DeviceHttpProxyPluginErrorType.deviceOffline]: 'I0004',
  [DeviceHttpProxyPluginErrorType.requestTimedOut]: 'I0005',
  [DeviceHttpProxyPluginErrorType.cancelled]: 'I0006',
  [DeviceHttpProxyPluginErrorType.unknownError]: 'I0007',
  [DeviceHttpProxyPluginErrorType.dataValveError]: 'I0008',
} as const satisfies Record<string, string>

/**
 * Map CMS errors.
 * Common CMS error messages mapped to specific error codes based on regex patterns.
 */
export const CMS_ERROR_CODES_MAPPING = [
  {
    regex: /requested entry doesn't exist/i,
    code: '00001',
  },
  {
    regex: /the content type/i,
    code: '00002',
  },
  {
    regex: /the requested object doesn't exist/i,
    code: '00003',
  },
  {
    regex: /language was not found/i,
    code: '00004',
  },
  {
    regex: /environment was not found/i,
    code: '00005',
  },
] as const satisfies ReadonlyArray<{
  readonly code: string
  readonly regex: RegExp
}>

/**
 * Error keys for each activation phase.
 * Used to define {@link APP_DEPENDENCY_ERRORS} for printer activation page.
 */
export const ACTIVATION_PHASE_ERROR_KEYS = {
  VERIFY: 'VERIFY_PHASE_FAILED',
  CONFIRM: 'CONFIRM_PHASE_FAILED',
  VALIDATE: 'VALIDATE_PHASE_FAILED',
  REGISTER: 'REGISTER_PHASE_FAILED',
  SHORTCUT_SCAN: 'SHORTCUT_SCAN_PHASE_FAILED',
  DEVICE_SHADOW: 'DEVICE_SHADOW_PHASE_FAILED',
  RTP: 'RTP_PHASE_FAILED',
} as const
export type ActivationPhaseErrorKey = typeof ACTIVATION_PHASE_ERROR_KEYS

/**
 * Map page render dependency errors.
 * Each page suffix should have specific error codes for known failure scenarios.
 */
export const APP_DEPENDENCY_ERRORS = {
  [PageSuffix.ASSIGN_WORKSPACE]: {
    GET_WORKSPACES_FAILED: '00001',
    REFRESH_TOKEN_FAILED: '00002',
    SET_TENANT_FAILED: '00003',
    GET_RESIDENCE_COUNTRY_FAILED: '00004',
  },
  [PageSuffix.ASSIGN_ORGANIZATION]: {
    GET_ORGANIZATIONS_FAILED: '00001',
    SET_TENANT_FAILED: '00002',
  },
  [PageSuffix.APP_VALUE_PROP]: {
    PROGRAM_NOT_FOUND: '00001',
    PLATFORM_NOT_SUPPORTED: '00002',
    PRINTER_NOT_SUPPORTED: '00003',
  },
  [PageSuffix.HP_ALL_IN]: {
    PAAS_ORDER_ID_MISSING: '00001',
    PAAS_ORDER_FAILED: '00002',
  },
  [PageSuffix.PRINTER_ACTIVATION]: {
    PAIRING_CODE_MISSING: '00001',
    FETCH_PHASES_FAILED: '00002',
    [ACTIVATION_PHASE_ERROR_KEYS.VERIFY]: 'STG01',
    [ACTIVATION_PHASE_ERROR_KEYS.CONFIRM]: 'STG02',
    [ACTIVATION_PHASE_ERROR_KEYS.VALIDATE]: 'STG03',
    [ACTIVATION_PHASE_ERROR_KEYS.REGISTER]: 'STG04',
    [ACTIVATION_PHASE_ERROR_KEYS.SHORTCUT_SCAN]: 'STG05',
    [ACTIVATION_PHASE_ERROR_KEYS.DEVICE_SHADOW]: 'STG06',
    [ACTIVATION_PHASE_ERROR_KEYS.RTP]: 'STG07',
  },
  [PageSuffix.PAIRING_CODE]: {
    BIZ_MODEL_MISMATCH: '00001',
  },
} as const satisfies Partial<
  Record<PageSuffix, { readonly [k: string]: string }>
>
export type AppDependencyErrors = typeof APP_DEPENDENCY_ERRORS

export const API_ERROR_CODES = {
  AM: {
    DEVICE_OFFLINE: 'C0001',
  },
} as const satisfies Partial<Record<ApiPrefix, Record<string, string>>>
export type ApiErrorCodes = typeof API_ERROR_CODES
